import React, { FunctionComponent, ReactChild, useMemo } from 'react'
import { useColorScheme } from 'react-native'
import { useSelector } from 'react-redux'

import { PRODUCT_TYPE } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/productType'
import {
  getEssentialsTheme,
  getProductTypeProviderValues,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/productTypeProviderUtils'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { useThemeSettings } from '@lyrahealth-inc/ui-core-crossplatform/src/hooks/useThemeSettings'
import { ProductTypeContext } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/productType/productTypeContext'

import { getCustomerProductType } from '../../data/customer/customerSelectors'

export const ProductTypeProvider: FunctionComponent<{
  children: ReactChild | ReactChild[]
}> = ({ children }) => {
  const { isWellbeingEnabled } = useFlags()
  const productType = useSelector(getCustomerProductType)
  const {
    isLyraSelect,
    isWellbeing,
    productType: computedProductType,
  } = getProductTypeProviderValues({
    isWellbeingEnabled,
    productType,
  })

  // Read through the shared theme store rather than localStorage directly so the Library iframe
  // reloads with the new theme as soon as the member changes it, and so `system` resolves against
  // the OS color scheme the same way the rest of the app does (CEA-3988).
  const { themeType } = useThemeSettings()
  const colorScheme = useColorScheme()

  const essentialsTheme = useMemo(
    () => getEssentialsTheme({ productType: computedProductType, themeType, colorScheme }),
    [colorScheme, computedProductType, themeType],
  )

  const value = useMemo(
    () => ({
      isLyraSelect,
      isWellbeing,
      isBend: computedProductType === PRODUCT_TYPE.BEND,
      productType: computedProductType,
      essentialsTheme,
    }),
    [computedProductType, essentialsTheme, isLyraSelect, isWellbeing],
  )

  return <ProductTypeContext.Provider value={value}>{children}</ProductTypeContext.Provider>
}
